import React, { useState, useEffect } from 'react';
import { Download, ExternalLink, Loader } from 'lucide-react';

/**
 * PDF Viewer using PDF.js in read-only mode
 * Annotation tools are disabled since they can't be saved to the server
 */
const PdfViewerWithFallback = ({ fileUrl, fileName }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Use PDF.js viewer from Mozilla - configured for read-only viewing
  // Note: The hosted viewer doesn't fully respect all parameters, but we try to minimize annotation UI
  const pdfjsViewerUrl = `https://mozilla.github.io/pdf.js/web/viewer.html?file=${encodeURIComponent(fileUrl)}`;
  
  useEffect(() => {
    setLoading(true);
    setError(false);
  }, [fileUrl]);

  const handleIframeLoad = () => {
    setLoading(false);
    // The PDF has loaded successfully
    console.log('PDF.js viewer loaded successfully');
  };

  const handleIframeError = () => {
    setLoading(false);
    setError(true);
    console.error('Failed to load PDF in viewer');
  };

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: '40px',
        textAlign: 'center',
        backgroundColor: '#f3f4f6'
      }}>
        <h3 style={{ marginBottom: '16px', color: '#111827' }}>Unable to Load PDF</h3>
        <p style={{ color: '#6b7280', marginBottom: '24px', maxWidth: '400px' }}>
          The PDF viewer couldn't load this file. You can try opening it in a new tab or downloading it.
        </p>
        <div style={{ display: 'flex', gap: '12px' }}>
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '12px 24px',
              backgroundColor: '#3b82f6',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '6px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <ExternalLink size={20} />
            Open in New Tab
          </a>
          <a
            href={fileUrl}
            download={fileName}
            style={{
              padding: '12px 24px',
              backgroundColor: '#10b981',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '6px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Download size={20} />
            Download PDF
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Simple header with download option */}
      <div style={{
        padding: '8px 12px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: '#f9fafb'
      }}>
        <div style={{ 
          fontSize: '13px', 
          color: '#6b7280',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {loading && (
            <>
              <Loader size={14} className="animate-spin" />
              Loading PDF...
            </>
          )}
          {!loading && (
            <>PDF Viewer (Read-Only)</>
          )}
        </div>
        
        <div style={{ display: 'flex', gap: '8px' }}>
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '4px 8px',
              fontSize: '12px',
              color: '#6b7280',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              backgroundColor: 'white'
            }}
            title="Open in new tab"
          >
            <ExternalLink size={14} />
          </a>
          <a
            href={fileUrl}
            download={fileName}
            style={{
              padding: '4px 8px',
              fontSize: '12px',
              color: '#6b7280',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              backgroundColor: 'white'
            }}
            title="Download PDF"
          >
            <Download size={14} />
          </a>
        </div>
      </div>

      {/* PDF Display Area */}
      <div style={{ flex: 1, position: 'relative', backgroundColor: '#525252' }}>
        {loading && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
            color: 'white'
          }}>
            <Loader size={32} className="animate-spin" />
            <span>Loading PDF...</span>
          </div>
        )}
        
        <iframe
          src={pdfjsViewerUrl}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            opacity: loading ? 0 : 1,
            transition: 'opacity 0.3s'
          }}
          title={fileName}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
        />
      </div>

    </div>
  );
};

export default PdfViewerWithFallback;